import { AvatarWithProgress } from "@circle-react/components/Leaderboard/Dashboard/MyProfile/AvatarWithProgress";
import { LevelBadge } from "@circle-react/components/Leaderboard/DashboardUIKit/MyProfile/LevelBadge";
import { useGamificationMember } from "@circle-react/components/Profile/HoverCard/useGamificationMember";
import { useGamificationSettings } from "@circle-react/components/SettingsApp/Gamification/gamificationApi";
import * as HoverCard from "@radix-ui/react-hover-card";
import { forwardRef } from "react";

import { Typography } from "@/react/components/SettingsApp/CompassCompat/Typography";
import { classNames } from "@/react/helpers/twMergeWithCN";

import { ActionButtons } from "./ActionButtons";
import type { ProfileHoverCardProps } from "./HoverCard";

interface CardContentProps extends Omit<ProfileHoverCardProps, "children"> {
  closeCard: () => void;
}

export const CardContent = forwardRef<HTMLDivElement, CardContentProps>(
  (
    {
      communityMemberId,
      publicUid,
      sqid,
      messagingEnabled,
      avatarURL,
      name = "",
      headline = "",
      side,
      sideOffset,
      closeCard,
    },
    ref,
  ) => {
    const { data: gamificationSettings } = useGamificationSettings();

    const { isPending, data } = useGamificationMember({
      member_ids: [communityMemberId],
    });

    const gamificationMember = data?.find(
      member =>
        Number(member.community_member_id) === Number(communityMemberId),
    );

    const shouldShowLevelBadge = !!gamificationSettings?.enabled;

    return (
      <HoverCard.Content
        ref={ref}
        className="border-primary bg-primary z-[1061] w-full min-w-[350px] max-w-[360px] rounded-lg border p-6 shadow-xl"
        sideOffset={sideOffset}
        side={side}
      >
        <div
          className={classNames(
            "flex justify-between",
            headline ? "items-start" : "items-center",
          )}
        >
          <div className="text-dark mr-4 truncate">
            <Typography.LabelLg weight="semibold">
              <span className="text-dark">{name}</span>
            </Typography.LabelLg>
            <br />
            {headline && (
              <div className="text-light truncate">
                <Typography.LabelSm weight="regular">
                  <span className="text-light">{headline}</span>
                </Typography.LabelSm>
              </div>
            )}
            {shouldShowLevelBadge && (
              <div className="mt-2">
                {isPending ? (
                  <div className="bg-tertiary mb-2 h-7 w-32 animate-pulse rounded-full text-center text-transparent md:text-left" />
                ) : (
                  gamificationMember?.current_level && (
                    <LevelBadge
                      level={gamificationMember?.current_level}
                      levelName={gamificationMember?.current_level_name}
                    />
                  )
                )}
              </div>
            )}
          </div>
          <div className="flex flex-1 justify-end">
            <AvatarWithProgress
              avatarUrl={avatarURL}
              name={name}
              size="small"
              progress={gamificationMember?.level_progress}
              level={gamificationMember?.current_level}
            />
          </div>
        </div>
        <div className="mt-4 space-y-2" />
        <ActionButtons
          communityMemberId={communityMemberId}
          publicUid={publicUid}
          sqid={sqid}
          messagingEnabled={messagingEnabled}
          closeCard={closeCard}
        />
      </HoverCard.Content>
    );
  },
);

CardContent.displayName = "CardContent";
